import React, { useState, useEffect } from "react";
import { tr } from '@/i18n/tr';
import { AlertTriangle, Clock, X, MessageCircle } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import axios, { API } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { useSaasPlans, planName } from "@/utils/saasPlans";

const WHATSAPP = "584248476761";

// tenant.plan / lifetime_package → saasPlans key
const PLAN_KEY = { lifetime_1: "basico", lifetime_2: "negocio", lifetime_4: "empresa", lifetime_6: "insignia", lifetime_5: "empresa",
  basic: "basico", pro: "negocio", enterprise: "empresa", standard: "basico" };

const PRICE_T = {
  zh: { once: "一次买断 · 终身授权", maint: "年维护", maintOf: (n) => `${n} 年维护费`, perYear: "/年" },
  es: { once: "Pago único · licencia de por vida", maint: "Mantenimiento anual", maintOf: (n) => `Mantenimiento anual ${n}`, perYear: "/año" },
  en: { once: "One-time payment · lifetime license", maint: "Annual maintenance", maintOf: (n) => `${n} annual maintenance`, perYear: "/yr" },
  pt: { once: "Pagamento único · licença vitalícia", maint: "Manutenção anual", maintOf: (n) => `Manutenção anual ${n}`, perYear: "/ano" },
};

function PriceBox({ lang, plans, isTrial, planId }) {
  const pt = PRICE_T[lang] || PRICE_T.es;
  const mine = !isTrial && plans.find((p) => p.key === PLAN_KEY[planId]);
  if (mine) {
    return (
      <p className="text-sm text-emerald-300 font-semibold text-center" data-testid="expiry-price-line">
        {pt.maintOf(planName(mine.key, lang))}: ${mine.mant}{pt.perYear}
      </p>
    );
  }
  const mants = plans.map((p) => p.mant).filter((m) => m > 0);
  const mantRange = mants.length ? (Math.min(...mants) === Math.max(...mants) ? `$${mants[0]}` : `$${Math.min(...mants)} – $${Math.max(...mants)}`) : "";
  return (
    <div data-testid="expiry-price-line">
      <p className="text-[11px] text-slate-400 text-center mb-1.5">{pt.once}</p>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1">
        {plans.map((p) => (
          <div key={p.key} className="flex justify-between text-xs">
            <span className="text-slate-300">{planName(p.key, lang)}</span>
            <span className="text-emerald-300 font-semibold">${p.setup}</span>
          </div>
        ))}
      </div>
      {mantRange && <p className="text-[11px] text-slate-400 text-center mt-2">+ {pt.maint} {mantRange}{pt.perYear}</p>}
    </div>
  );
}

const T = {
  zh: {
    expiredTitle: tr('x_4a07f0c8'), expiredMsg: tr('x_db45a2da'),
    soonTitle: tr('x_5eeb04af'), soonMsg: (d) => `${tr('x_35b7c240')} ${d} ${tr('x_aa277e9b')}`,
    trialTitle: tr('x_8dd2a3dc'), trialMsg: (d) => `${tr('x_7dc5867e')} ${d} ${tr('x_1d74fc6c')}`,
    trialExpired: tr('x_01059b1d'), trialExpiredMsg: tr('x_26a5e5a8'),
    renew: tr('x_39bb592b'), later: tr('x_dc017114'),
  },
  es: {
    expiredTitle: "Suscripción vencida", expiredMsg: "Tu suscripción de Sellox venció. Renueva pronto para no afectar tu negocio.",
    soonTitle: "Suscripción por vencer", soonMsg: (d) => `Tu suscripción vence en ${d} días. Renueva a tiempo para no interrumpir el servicio.`,
    trialTitle: "Tu prueba está por terminar", trialMsg: (d) => `Quedan ${d} días de prueba gratis. Activa tu licencia para seguir usando todo.`,
    trialExpired: "Prueba gratis terminada", trialExpiredMsg: "Activa tu licencia para desbloquear todas las funciones y seguir vendiendo.",
    renew: "Renovar ahora (WhatsApp)", later: "Recordar luego",
  },
  en: {
    expiredTitle: "Subscription expired", expiredMsg: "Your Sellox subscription has expired. Renew soon to keep your business running.",
    soonTitle: "Subscription expiring", soonMsg: (d) => `Your subscription expires in ${d} days. Renew in time to avoid interruption.`,
    trialTitle: "Trial ending soon", trialMsg: (d) => `${d} days left in your free trial. Activate your license to keep all features.`,
    trialExpired: "Free trial ended", trialExpiredMsg: "Activate your license to unlock all features and keep selling.",
    renew: "Renew now (WhatsApp)", later: "Remind me later",
  },
  pt: {
    expiredTitle: "Assinatura expirada", expiredMsg: "Sua assinatura Sellox expirou. Renove logo para não afetar o negócio.",
    soonTitle: "Assinatura a vencer", soonMsg: (d) => `Sua assinatura vence em ${d} dias. Renove a tempo para não interromper.`,
    trialTitle: "Teste terminando", trialMsg: (d) => `${d} dias restantes no teste grátis. Ative sua licença para manter tudo.`,
    trialExpired: "Teste grátis encerrado", trialExpiredMsg: "Ative sua licença para desbloquear tudo e continuar vendendo.",
    renew: "Renovar agora (WhatsApp)", later: "Lembrar depois",
  },
};

export default function SubscriptionExpiryModal() {
  const { lang } = useLang();
  const { user } = useAuth();
  const plans = useSaasPlans();
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!user?.tenant_id) return;
    axios.get(`${API}/tenant/my-usage`).then((r) => {
      const d = r.data || {};
      const isTrial = !!d.is_trial;
      const trialLeft = d.trial_days_left;
      // lifetime tenants: what expires is the yearly maintenance window, not the licence
      const subStatus = d.lifetime_active ? d.maintenance_status : d.subscription_status;
      const subLeft = d.lifetime_active ? d.maintenance_days_left : d.subscription_days_left;

      const expired = subStatus === "expired" || d.trial_expired === true;
      const soon = subStatus === "expiring_soon" && subLeft >= 0 && subLeft <= 7;
      const trialSoon = isTrial && typeof trialLeft === "number" && trialLeft <= 7 && !d.trial_expired;

      if (!expired && !soon && !trialSoon) return;

      // Expired → show every session (once per session). Soon → once per day.
      const key = `sub_expiry_modal_${user.tenant_id}`;
      if (!expired) {
        const last = localStorage.getItem(key);
        const today = new Date().toISOString().slice(0, 10);
        if (last === today) return;
        localStorage.setItem(key, today);
      } else {
        const skey = `sub_expiry_seen_session`;
        if (sessionStorage.getItem(skey)) return;
        sessionStorage.setItem(skey, "1");
      }
      setInfo({ expired, soon, trialSoon, isTrial, trialLeft, subLeft, planId: d.plan || "" });
      setOpen(true);
    }).catch(() => {});
  }, [user]);

  if (!info) return null;
  const tt = T[lang] || T.es;
  const { expired, isTrial, trialLeft, subLeft } = info;

  let title, msg, urgent = expired;
  if (expired) {
    title = isTrial ? tt.trialExpired : tt.expiredTitle;
    msg = isTrial ? tt.trialExpiredMsg : tt.expiredMsg;
  } else if (info.trialSoon) {
    title = tt.trialTitle; msg = tt.trialMsg(trialLeft);
  } else {
    title = tt.soonTitle; msg = tt.soonMsg(subLeft);
  }

  const waText = encodeURIComponent(
    (lang === "zh" ? tr('x_7693a3c0') : lang === "en" ? "Hi, I'd like to renew my Sellox subscription. Business: " : lang === "pt" ? "Olá, quero renovar minha assinatura Sellox. Empresa: " : "Hola, quiero renovar mi suscripción Sellox. Comercio: ") + (user?.tenant_name || "")
  );
  const waLink = `https://wa.me/${WHATSAPP}?text=${waText}`;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md border-0 p-0 overflow-hidden bg-slate-900" data-testid="subscription-expiry-modal">
        <div className={`p-6 ${urgent ? "bg-gradient-to-b from-red-600/25 to-slate-900" : "bg-gradient-to-b from-amber-600/25 to-slate-900"}`}>
          <div className="flex items-start gap-3">
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${urgent ? "bg-red-500/20" : "bg-amber-500/20"}`}>
              {urgent ? <AlertTriangle className="w-6 h-6 text-red-400" /> : <Clock className="w-6 h-6 text-amber-400" />}
            </div>
            <div className="flex-1">
              <h2 className={`text-lg font-bold ${urgent ? "text-red-300" : "text-amber-300"}`} data-testid="expiry-modal-title">{title}</h2>
              <p className="text-sm text-slate-300 mt-1.5 leading-relaxed">{msg}</p>
            </div>
          </div>

          <div className="mt-5 p-3 rounded-xl bg-slate-800/70 border border-white/10">
            <PriceBox lang={lang} plans={plans} isTrial={isTrial} planId={info.planId} />
          </div>

          <div className="mt-5 flex flex-col gap-2">
            <a href={waLink} target="_blank" rel="noreferrer" data-testid="expiry-renew-btn">
              <Button className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold gap-2">
                <MessageCircle className="w-4 h-4" /> {tt.renew}
              </Button>
            </a>
            {!urgent && (
              <Button variant="ghost" onClick={() => setOpen(false)} className="w-full text-slate-400 hover:text-white text-sm" data-testid="expiry-later-btn">
                {tt.later}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
